/** Load chat billing independently of rendered history and the workspace wallet. */
import { API_BASE, getAuthHeaders } from '../../utils/supabase/client';

export type SIUsageSummary = {
 balanceGbp: number | null;
 chargingEnabled: boolean;
};
export type SIChatUsageScope = { conversationId: string } & (
 { workspaceId: string; projectId?: never } | { projectId: string; workspaceId?: never }
);
export type SIChatUsageSummary = {
 inputTokens: number | null; outputTokens: number | null; totalTokens: number | null;
 knownTotalTokens: number; unknownTokenRequests: number;
 actualHostingCostGbp: number | null; actualUsagePriceGbp: number | null;
 hostingCostGbp?: number | null; usagePriceGbp?: number | null;
 costBasis?: 'billed' | 'accrued' | 'mixed' | 'unavailable' | 'no_usage';
 billedRequests?: number; accruedRequests?: number; unpricedRequests?: number;
 accrualSourceAsOf?: string | null;
 grossMarginPercentage: number;
 costStatus: 'pending' | 'reconciled' | 'no_usage';
 pendingRequests: number; requests: number; costUpdatedAt: string | null;
 allocationMethod: 'billed_instance_time_share';
};
export async function loadSIUsage(workspaceId: string, signal: AbortSignal): Promise<SIUsageSummary> {
 const response = await fetch(`${API_BASE}/si/usage?workspaceId=${encodeURIComponent(workspaceId)}`, { headers: await getAuthHeaders(), signal });
 if (!response.ok) throw new Error('Usage unavailable');
 return response.json() as Promise<SIUsageSummary>;
}
const costFormat = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const smallCostFormat = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 6 });
export function formatSICost(value: number | null | undefined): string {
 if (typeof value !== 'number' || !Number.isFinite(value)) return 'Unavailable';
 if (value === 0) return costFormat.format(0);
 // The API uses whole micro-GBP; preserve a truthful bound for smaller inputs.
 if (Math.abs(value) < 0.000001) return value < 0 ? '>-£0.000001' : '<£0.000001';
 return (Math.abs(value) >= 0.01 ? costFormat : smallCostFormat).format(value);
}

export async function loadChatUsage(scope: SIChatUsageScope, signal: AbortSignal): Promise<SIChatUsageSummary> {
 const query = new URLSearchParams({ conversationId: scope.conversationId });
 if (typeof scope.projectId === 'string') query.set('projectId', scope.projectId);
 else query.set('workspaceId', scope.workspaceId);
 const response = await fetch(`${API_BASE}/si/chat-usage?${query}`, { headers: await getAuthHeaders(), signal });
 if (!response.ok) throw new Error('Chat usage could not be loaded.');
 return response.json() as Promise<SIChatUsageSummary>;
}
